export interface ScriptLine {
  text: string;
  kind: 'heading' | 'action' | 'dialogue' | 'transition';
}

export interface SceneBlock {
  id: string;
  title: string;
  lines: ScriptLine[];
  estimatedSeconds: number;
}

export const SCENE_MARKER = /^(cena|scene|int\.|ext\.|int\.\/ext\.|int\/ext)[\s.:-]/i;

export const TITLE_MARKER = /^(t[ií]tulo|title)\s*:/i;

export const TRANSITION_MARKER = /^(corte|corta|fade|dissolve|cut to|fade to)/i;

export const DIALOGUE_MARKER = /^([A-ZÀ-Ú0-9\s-]{2,})\s*[:(]/;

export const SPEAKER_MARKER = /^[A-ZÀ-Ú][A-ZÀ-Ú\s-]{1,24}$/;

export function classifyLine(text: string): ScriptLine['kind'] {
  const trimmed = text.trim();
  if (!trimmed) return 'action';
  if (SCENE_MARKER.test(trimmed)) return 'heading';
  if (TRANSITION_MARKER.test(trimmed)) return 'transition';
  if (DIALOGUE_MARKER.test(trimmed)) return 'dialogue';
  return 'action';
}

export function splitSceneBlocks(script: string): string[] {
  const normalized = script.replace(/\r\n/g, '\n');
  const rawLines = normalized.split('\n');
  const hasMarkers = rawLines.some((line) => SCENE_MARKER.test(line.trim()));

  if (!hasMarkers) {
    return normalized
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter((paragraph) => paragraph.length > 0);
  }

  const blocks: string[] = [];
  let current: string[] = [];

  const flush = () => {
    if (current.some((l) => l.trim().length > 0)) {
      blocks.push(current.join('\n').trim());
    }
    current = [];
  };

  for (const rawLine of rawLines) {
    const line = rawLine.trim();
    if (TITLE_MARKER.test(line)) continue;
    if (SCENE_MARKER.test(line) && current.length > 0) {
      flush();
    }
    current.push(line);
  }
  flush();
  return blocks.filter((b) => b.length > 0);
}

export function parseSceneBlock(block: string, index: number): SceneBlock {
  const lines = block
    .split('\n')
    .map((raw) => raw.trim())
    .filter((l) => l.length > 0)
    .map((text) => ({ text, kind: classifyLine(text) }));

  for (let i = 1; i < lines.length; i++) {
    if (lines[i].kind === 'action' && SPEAKER_MARKER.test(lines[i - 1].text)) {
      lines[i] = { ...lines[i], kind: 'dialogue' };
    }
  }

  const heading =
    lines.find((l) => SCENE_MARKER.test(l.text))?.text ?? lines[0]?.text ?? `Cena ${index + 1}`;

  return {
    id: `scene-${index + 1}`,
    title: heading,
    lines,
    estimatedSeconds: estimateSceneSeconds(lines),
  };
}

export function parseScript(script: string): SceneBlock[] {
  return splitSceneBlocks(script)
    .map(parseSceneBlock)
    .filter((scene) => scene.lines.length > 0);
}

export function estimateSceneSeconds(lines: ScriptLine[]): number {
  let seconds = 3;
  for (const line of lines) {
    if (line.kind === 'heading') seconds += 1.5;
    else if (line.kind === 'dialogue') seconds += Math.min(6, 1.5 + line.text.length / 14);
    else if (line.kind === 'transition') seconds += 0.8;
    else seconds += Math.min(8, 1.5 + line.text.length / 40);
  }
  return Math.round(seconds);
}

export function sceneTitle(scene: SceneBlock): string {
  const heading = scene.title.replace(SCENE_MARKER, '').trim();
  return heading || `Cena ${scene.id}`;
}
